/**
 * The footer disclosures: a centred row of buttons ("Settings", "Rules") with
 * one panel area above it, since the row sits at the foot of the column and
 * the space is above. Clicking a button opens its panel there; clicking it
 * again, another button, anywhere off the panel, or Escape closes it. A
 * native <details> per label would open each panel under its own label
 * instead.
 */

export interface Disclosure {
  label: string;
  body: HTMLElement;
  /** Called when this panel opens, and when it closes again. */
  onOpen?: () => void;
  onClose?: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createDisclosures(root: HTMLElement, items: readonly Disclosure[]): void {
  const row = el('div', 'disclosures__row');
  const panel = el('div', 'disclosures__panel');
  panel.hidden = true;

  const buttons = items.map((item, i) => {
    const button = el('button', 'disclosures__summary', item.label);
    button.type = 'button';
    button.setAttribute('aria-expanded', 'false');
    item.body.id = `disclosure-${i}`;
    button.setAttribute('aria-controls', item.body.id);
    return button;
  });

  let openIndex = -1;
  function show(index: number): void {
    if (index !== openIndex) {
      items[openIndex]?.onClose?.();
      items[index]?.onOpen?.();
    }
    openIndex = index;
    buttons.forEach((b, i) => b.setAttribute('aria-expanded', String(i === index)));
    panel.hidden = index < 0;
    if (index >= 0) panel.replaceChildren(items[index]!.body);
  }

  buttons.forEach((button, i) => {
    button.addEventListener('click', () => show(openIndex === i ? -1 : i));
    row.append(button);
  });

  // The panel floats over the yard, so it closes the way anything covering
  // the page does: a click anywhere off it puts it away. This listens on the
  // document in the bubble phase, after the buttons' own handlers, so a click
  // on the open button still toggles rather than closing and reopening.
  //
  // The path is read rather than the target, because it is fixed when the
  // event is dispatched and still names the panel even when the handler that
  // ran first replaced what was clicked. The mute button swaps its icon on
  // click, which detaches the very element the event came from; asking
  // whether the panel still contains it answers no, and the panel shut itself
  // every time someone muted.
  document.addEventListener('click', (ev) => {
    if (openIndex < 0) return;
    const path = ev.composedPath();
    if (path.includes(panel) || path.includes(row)) return;
    show(-1);
  });

  // Escape is the keyboard's version of clicking away. Focus goes back to
  // the button that opened the panel, so tabbing carries on from there
  // rather than starting again at the top of the page.
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || openIndex < 0) return;
    const button = buttons[openIndex];
    show(-1);
    button?.focus();
  });

  root.append(panel, row);
}
